import { dashboard } from 'types';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const { SeatingTemplateInsertRequest, SeatingTemplate, SeatingTemplateCopyRequest } = protos;

/**
 * Fetches all templates and tickets to be able to render the template screen.
 * @returns {Object} A redux TockApi action.
 */
export function fetchAll(): ApiAction {
  return {
    TockApi: {
      batchProto: [
        {
          path: '/seating/template',
          method: 'GET',
        },
        {
          path: '/ticket/type',
          method: 'GET',
        },
      ],
      type: AppConstants.TEMPLATE_FETCH_ALL,
    },
  };
}

/**
 * Adds a seating template.
 *
 * @param {SeatingTemplate} seatingTemplate The seating template to add.
 * @param {number} defaultTicketTypeIdForFlex Ticket type id for a flex blueprint
 * @returns {Object} A redux TockApi action.
 */
export function add(seatingTemplate: dashboard.SeatingTemplate): ApiAction {
  const requestBody = SeatingTemplateInsertRequest.fromObject({
    seatingTemplate,
  });
  return addInsertRequest(requestBody);
}

/**
 * Adds a seating template.
 */
export function addInsertRequest(
  insertRequest: dashboard.SeatingTemplateInsertRequest,
  showToast = true
): ApiAction {
  return {
    TockApi: {
      postProto: '/seating/template',
      type: AppConstants.TEMPLATE_ADD,
      request: insertRequest,
      toast: showToast ? 'Blueprint created' : undefined,
    },
  };
}

/**
 * Copy an existing seating template in an inventory schedule to another day.
 * @param seatingTemplateId The seating template to import.
 * @param businessInventoryScheduleId The schedule to import it into.
 * @param startTime Start time of resulting seating template.
 * @param endTime End time of resulting seating template.
 * @param serviceType Service type of resulting seating template
 * @param [defaultTicketTypeId]
 */
export function copyInventorySchedule(
  seatingTemplateId: number,
  businessInventoryScheduleId: number,
  startTime: string,
  endTime: string,
  serviceType: string,
  defaultTicketTypeId?: number
): ApiAction {
  return {
    TockApi: {
      postProto: `/seating/template/${seatingTemplateId}/copy`,
      type: AppConstants.TEMPLATE_ADD,
      request: SeatingTemplateCopyRequest.fromObject({
        startTime,
        endTime,
        businessInventoryScheduleId,
        defaultTicketTypeId,
        serviceType,
        useAllDayService: serviceType === 'ALL_DAY',
      }),
      toast: 'Service cloned',
    },
  };
}

/**
 * Import an existing seating template into an inventory schedule.
 * @param seatingTemplateId The seating template to import.
 * @param businessInventoryScheduleId The schedule to import it into.
 * @param startTime Start time of resulting seating template.
 * @param endTime End time of resulting seating template.
 */
export function importIntoInventorySchedule(
  seatingTemplateId: number,
  businessInventoryScheduleId: number,
  startTime: string,
  endTime: string,
  defaultTicketTypeId?: number
): ApiAction {
  return {
    TockApi: {
      postProto: `/seating/template/${seatingTemplateId}/copy`,
      type: AppConstants.TEMPLATE_ADD,
      request: SeatingTemplateCopyRequest.fromObject({
        startTime,
        endTime,
        businessInventoryScheduleId,
        defaultTicketTypeId,
      }),
      toast: 'Blueprint imported',
    },
  };
}

/**
 * Archive the given seating template.
 *
 * @param {SeatingTemplate} seatingTemplate The seating template to update.
 * @returns {Object} A redux TockApi action.
 */
export function archive(seatingTemplate: dashboard.SeatingTemplate): ApiAction {
  seatingTemplate.isArchived = true;
  return {
    TockApi: {
      putProto: '/seating/template',
      type: AppConstants.TEMPLATE_UPDATE,
      request: SeatingTemplate.fromObject(seatingTemplate),
      toast: 'Blueprint archived',
    },
  };
}

/**
 * Unarchive the given seating template.
 *
 * @param {SeatingTemplate} seatingTemplate The seating template to update.
 * @returns {Object} A redux TockApi action.
 */
export function unarchive(seatingTemplate: dashboard.SeatingTemplate): ApiAction {
  seatingTemplate.isArchived = false;
  return {
    TockApi: {
      putProto: '/seating/template',
      type: AppConstants.TEMPLATE_UPDATE,
      request: SeatingTemplate.fromObject(seatingTemplate),
      toast: 'Blueprint unarchived',
    },
  };
}

/**
 * Download JSON export of a seating template by ID.
 */
export function exportSeatingTemplate(id: number): ApiAction {
  return {
    TockApi: {
      getRawResponse: `/seating/template/${id}/export`,
      ignoreDispatch: true,
      type: AppConstants.NULL,
    },
  };
}

export function importSeatingTemplate(file: File): ApiAction {
  return {
    TockApi: {
      postFile: `/seating/template/import`,
      type: AppConstants.NULL,
      request: file,
      ignoreDispatch: true,
    },
  };
}
